import { GetServerSideProps } from 'next';
import CommonExperienceDetailPage from '@/components/experience-detail/CommonExperienceDetailPage';

export const getServerSideProps: GetServerSideProps = async context => {
  const { slug } = context.params as { slug: string };
  const bookingId = context.query?.booking as string | undefined;
  // Note: 'slug' parameter now contains the ID
  const id = slug; // Renaming for clarity

  try {
    const BASE_URL = process.env.NEXT_PUBLIC_API_URL;
    const detailsUrl = bookingId
      ? `${BASE_URL}/stay-experiences/v1/${id}?booking=${encodeURIComponent(bookingId)}`
      : `${BASE_URL}/stay-experiences/v1/${id}`;

    // Fetch main stay experience data using ID
    const mainResponse = await fetch(detailsUrl);
    const mainData = await mainResponse.json();

    if (!mainData.data) {
      return {
        props: {
          initialData: null,
          slug: id,
          type: 'stay' as const,
          ...(bookingId && { bookingId }),
        },
      };
    }

    const stayExperience = mainData.data;

    // Fetch additional data in parallel using ID
    const stayId = stayExperience._id;

    const [pricingRes, activitiesRes, roomOptionsRes] =
      await Promise.allSettled([
        fetch(`${BASE_URL}/pricing/v1/entity/stay-experience/${stayId}`),
        fetch(`${BASE_URL}/activities/v1/parent/${stayId}`),
        fetch(`${BASE_URL}/stay-experiences/packages/v1/experience/${stayId}`),
      ]);

    // Parse responses
    const pricingData =
      pricingRes.status === 'fulfilled' && pricingRes.value.ok
        ? await pricingRes.value.json().then(d => d.data || {})
        : {};

    const activitiesData =
      activitiesRes.status === 'fulfilled' && activitiesRes.value.ok
        ? await activitiesRes.value.json().then(d => d.data?.activities || [])
        : [];

    const roomOptionsData =
      roomOptionsRes.status === 'fulfilled' && roomOptionsRes.value.ok
        ? await roomOptionsRes.value.json().then(d => d.data?.packages || [])
        : [];

    // Extract and format guide details from the guide object
    const guideData = stayExperience?.guide;
    const guideDetails =
      guideData && typeof guideData === 'object' && guideData._id
        ? {
            _id: guideData._id,
            userId: guideData.userId || '',
            name: guideData.name || '',
            contactEmail: guideData.contactEmail || '',
            contactPhone: guideData.contactPhone || 0,
            companyName: guideData.companyName || null,
            address: guideData.address || null,
            type: guideData.type || null,
            profileDescription: guideData.profileDescription || null,
            languages: guideData.languages || null,
            imageUrl: guideData.imageUrl || null,
            nexgoVerified: guideData.nexgoVerified || null,
            rating: guideData.rating || null,
            guideIntroVideoUrl: guideData.guideIntroVideoUrl || null,
          }
        : null;

    // Enrich the stay experience with additional data
    const enrichedActivity = {
      ...stayExperience,
      pricing: pricingData,
      activities: activitiesData,
      roomOptions: roomOptionsData,
      guideDetails: guideDetails,
    };

    // Remove undefined values to prevent serialization errors
    const serializedActivity = JSON.parse(JSON.stringify(enrichedActivity));

    return {
      props: {
        initialData: serializedActivity,
        slug: id,
        type: 'stay' as const,
        ...(bookingId && { bookingId }),
      },
    };
  } catch (error) {
    console.error('Error fetching stay experience:', error);
    return {
      props: {
        initialData: null,
        slug: id,
        type: 'stay' as const,
        ...(bookingId && { bookingId }),
      },
    };
  }
};

interface StayExperienceDetailsProps {
  initialData: unknown;
  slug: string;
  type: 'stay';
  bookingId?: string;
}

export default function StayExperienceDetails({
  initialData,
  slug,
  type,
  bookingId,
}: StayExperienceDetailsProps) {
  return (
    <CommonExperienceDetailPage
      initialData={initialData}
      slug={slug}
      type={type}
      bookingId={bookingId}
    />
  );
}
